import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// features
import {
  createPendingActivitySuccess as driverCardsCreatePendingActivitySuccess,
  updatePendingActivitySuccess as driverCardsUpdatePendingActivitySuccess,
  removePendingActivitySuccess as driverCardsRemovePendingActivitySuccess,
  createUnavailablePeriodSuccess as driverCardsCreateUnavailablePeriodSuccess,
  updateUnavailablePeriodSuccess as driverCardsUpdateUnavailablePeriodSuccess,
  removeUnavailablePeriodSuccess as driverCardsRemoveUnavailablePeriodSuccess,
} from '../drivers-card/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature fleet-profile-simple
import * as A from './actions';
//////////////////////////////////////////////////

const initialState = {
  truckGuid: null,
  groupGuid: null,
  openedFrom: null,
  truckProfile: {},
  driverProfiles: {},
  trailerProfile: {},
  dispatcherGuid: null,
  driverProfileGuid: null,
  openedDriverProfile: false,
  driverPendingActivities: [],
  driverUnavailablePeriods: [],
  driverAvailabilityStatus: null,
};

const setInitialState = () => initialState;

const setValueToStore = (state, { path, value }) => (
  P.$set(path, value, state)
);

const openDriverProfile = (
  state,
  {
    groupGuid,
    truckGuid,
    openedFrom,
    dispatcherGuid,
    driverProfileGuid,
    pendingActivities = [],
    unavailablePeriods = [],
  }) => (
  P.$all(
    P.$set('truckProfile', {}),
    P.$set('trailerProfile', {}),
    P.$set('groupGuid', groupGuid),
    P.$set('truckGuid', truckGuid),
    P.$set('openedFrom', openedFrom),
    P.$set('openedDriverProfile', true),
    P.$set('dispatcherGuid', dispatcherGuid),
    P.$set('driverProfileGuid', driverProfileGuid),
    P.$set('driverPendingActivities', pendingActivities),
    P.$set('driverUnavailablePeriods', unavailablePeriods),
    state,
  )
);

const closeDriverProfile = (state) => (
  P.$all(
    P.$set('groupGuid', null),
    P.$set('truckGuid', null),
    P.$set('openedFrom', null),
    P.$set('dispatcherGuid', null),
    P.$set('driverProfileGuid', null),
    P.$set('openedDriverProfile', false),
    P.$set('driverPendingActivities', []),
    P.$set('driverUnavailablePeriods', []),
    state,
  )
);

const closeFleetProfile = ({ driverProfiles }) => ({ ...initialState, driverProfiles });

const openFleetProfile = (state, data) => ({ ...state, ...data });

const getDriverProfileInfoSuccess = (state, data) => {
  const { guid } = data;

  if (G.isNilOrEmpty(guid)) return state;

  const driverProfile = R.mergeRight(data, { openedFuelCards: false, openedCertificates: false });

  return P.$set(`driverProfiles.${guid}`, driverProfile, state);
};

const toggleDriverCertificates = (state, driverGuid) => (
  P.$toggle(`driverProfiles.${driverGuid}.openedCertificates`, state)
);

const getDriverCertificatesSuccess = (state, { driverGuid, certificates }) => {
  if (G.isNilOrEmpty(driverGuid)) return state;
  const { driverProfiles } = state;

  const newProfile = R.mergeRight(
    G.getPropFromObject(driverGuid, driverProfiles),
    { certificates, openedCertificates: true },
  );

  return P.$set(`driverProfiles.${driverGuid}`, newProfile, state);
};

const toggleDriverFuelCards = (state, driverGuid) => (
  P.$toggle(`driverProfiles.${driverGuid}.openedFuelCards`, state)
);

const getDriverFuelCardsSuccess = (state, { fuelCards, driverGuid }) => {
  if (G.isNilOrEmpty(driverGuid)) return state;

  const { driverProfiles } = state;

  const newProfile = R.mergeRight(
    G.getPropFromObject(driverGuid, driverProfiles),
    { fuelCards, openedFuelCards: true },
  );

  return P.$set(`driverProfiles.${driverGuid}`, newProfile, state);
};

const toggleDriverFuelCardSuccess = (state, { guid, driverGuid }) => {
  const index = R.findIndex(
    R.propEq(guid, GC.FIELD_GUID),
    R.pathOr([], ['driverProfiles', driverGuid, 'fuelCards'], state),
  );

  return P.$toggle(`driverProfiles.${driverGuid}.fuelCards.${index}.enabled`, state);
};

const getDriverAvailabilityStatusSuccess = (state, { status }) => (
  P.$set('driverAvailabilityStatus', status, state)
);

// pending activities
const createPendingActivitySuccess = (state, data) => {
  const { driverPendingActivities } = state;

  const newPeriods = R.append(data, driverPendingActivities);

  return P.$set('driverPendingActivities', newPeriods, state);
};

const updatePendingActivitySuccess = (state, data) => {
  const { driverPendingActivities } = state;
  const { guid } = data;

  const newPeriods = R.map(
    (item) => {
      const itemGuid = G.getGuidFromObject(item);

      if (R.equals(guid, itemGuid)) return data;

      return item;
    },
    driverPendingActivities,
  );

  return P.$set('driverPendingActivities', newPeriods, state);
};

const removePendingActivitySuccess = (state, data) => {
  const { driverPendingActivities } = state;
  const { guid } = data;

  const newPeriods = R.reject(R.propEq(guid, GC.FIELD_GUID), driverPendingActivities);

  return P.$set('driverPendingActivities', newPeriods, state);
};

// unavailable periods
const createUnavailablePeriodSuccess = (state, data) => {
  const { driverUnavailablePeriods } = state;

  const newPeriods = R.append(data, driverUnavailablePeriods);

  return P.$set('driverUnavailablePeriods', newPeriods, state);
};

const updateUnavailablePeriodSuccess = (state, data) => {
  const { driverUnavailablePeriods } = state;
  const { guid } = data;

  const newPeriods = R.map(
    (item) => {
      const itemGuid = G.getGuidFromObject(item);

      if (R.equals(guid, itemGuid)) return data;

      return item;
    },
    driverUnavailablePeriods,
  );

  return P.$set('driverUnavailablePeriods', newPeriods, state);
};

const removeUnavailablePeriodSuccess = (state, data) => {
  const { driverUnavailablePeriods } = state;
  const { guid } = data;

  const newPeriods = R.reject(R.propEq(guid, GC.FIELD_GUID), driverUnavailablePeriods);

  return P.$set('driverUnavailablePeriods', newPeriods, state);
};

// truck
const getTruckProfileSuccess = (state, data) => (
  P.$set('truckProfile.generalDetails', data, state)
);

const getAssignInfoSuccess = (state, { profileType, ...data }) => (
  P.$set(`${profileType}Profile.assignInfo`, data, state)
);

const getTruckLatestKnownLocationSuccess = (state, data) => (
  P.$set('truckProfile.latestLocation', data, state)
);

const getTruckDocumentListSuccess = (state, data) => (
  P.$set('truckProfile.documents', data, state)
);

// trailer
const getTrailerProfileSuccess = (state, data) => (
  P.$set('trailerProfile.generalDetails', data, state)
);

const getTrailerLatestKnownLocationSuccess = (state, data) => (
  P.$set('trailerProfile.latestLocation', data, state)
);

const getTrailerDocumentListSuccess = (state, data) => (
  P.$set('trailerProfile.documents', data, state)
);

export default createReducer({
  [A.setInitialState]: setInitialState,
  [A.setValueToStore]: setValueToStore,
  [A.openFleetProfile]: openFleetProfile,
  [A.openDriverProfile]: openDriverProfile,
  [A.closeFleetProfile]: closeFleetProfile,
  [A.closeDriverProfile]: closeDriverProfile,
  [A.toggleDriverFuelCards]: toggleDriverFuelCards,
  [A.toggleDriverCertificates]: toggleDriverCertificates,
  [A.getDriverFuelCardsSuccess]: getDriverFuelCardsSuccess,
  [A.getDriverProfileInfoSuccess]: getDriverProfileInfoSuccess,
  [A.toggleDriverFuelCardSuccess]: toggleDriverFuelCardSuccess,
  [A.getDriverCertificatesSuccess]: getDriverCertificatesSuccess,
  [driverCardsCreatePendingActivitySuccess]: createPendingActivitySuccess,
  [driverCardsUpdatePendingActivitySuccess]: updatePendingActivitySuccess,
  [driverCardsRemovePendingActivitySuccess]: removePendingActivitySuccess,
  [A.getDriverAvailabilityStatusSuccess]: getDriverAvailabilityStatusSuccess,
  [driverCardsCreateUnavailablePeriodSuccess]: createUnavailablePeriodSuccess,
  [driverCardsUpdateUnavailablePeriodSuccess]: updateUnavailablePeriodSuccess,
  [driverCardsRemoveUnavailablePeriodSuccess]: removeUnavailablePeriodSuccess,
  // truck
  [A.getAssignInfoSuccess]: getAssignInfoSuccess,
  [A.getTruckProfileSuccess]: getTruckProfileSuccess,
  [A.getTruckDocumentListSuccess]: getTruckDocumentListSuccess,
  [A.getTruckLatestKnownLocationSuccess]: getTruckLatestKnownLocationSuccess,
  // trailer
  [A.getTrailerProfileSuccess]: getTrailerProfileSuccess,
  [A.getTrailerDocumentListSuccess]: getTrailerDocumentListSuccess,
  [A.getTrailerLatestKnownLocationSuccess]: getTrailerLatestKnownLocationSuccess,
}, initialState);
